// src/pages/LMS/Enrollments/EnrollmentProgress.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  Quiz as QuizIcon,
  School as SchoolIcon,
  Timer as TimerIcon,
  EmojiEvents as TrophyIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';

const EnrollmentProgress = () => {
  const { chatbotId, enrollmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);

  useEffect(() => {
    fetchProgressData();
  }, [enrollmentId]);

  const fetchProgressData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Axios already parses JSON, no need for .json()
      const response = await axiosInstance.get(`/api/lms/enrollments/${enrollmentId}/progress`);
      
      console.log('Progress response:', response.data);

      if (response.data.success) {
        setProgressData(response.data.data);
        
        // Auto-expand first incomplete module
        const firstIncomplete = response.data.data.modules?.find(m => !m.progress?.completed);
        if (firstIncomplete) {
          setExpandedModule(firstIncomplete.id);
        }
      } else {
        setError(response.data.message || 'Failed to load progress data');
      }
    } catch (err) {
      console.error('Error fetching progress:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleExpand = (moduleId) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'enrolled':
        return 'default';
      case 'dropped':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return 'UNKNOWN';
    return status.replace('_', ' ').toUpperCase();
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video':
        return <VideoLibraryIcon />;
      case 'document':
        return <DescriptionIcon />;
      case 'slide':
        return <PlayCircleOutlineIcon />;
      default:
        return <DescriptionIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 14, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/lms/enrollments`)}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (!progressData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 14, mb: 4 }}>
        <Alert severity="info">No progress data found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/lms/enrollments`)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  const { enrollment, course, student, modules } = progressData;

  return (
    <Container maxWidth="lg" sx={{ mt: 14, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/lms/enrollments`)}
          sx={{ mb: 2 }}
        >
          Back to Enrollments
        </Button>
        
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h4" gutterBottom>
                {course?.title || 'Course Progress'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enrolled on {formatDate(enrollment?.enrolled_at)}
              </Typography>
              <Chip 
                label={getStatusLabel(enrollment?.status)} 
                color={getStatusColor(enrollment?.status)}
                size="small"
                sx={{ mt: 1 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} gutterBottom>
                Student: {student?.first_name} {student?.last_name} ({student?.student_identity})
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box textAlign="right">
                <Typography variant="h2" color="primary">
                  {enrollment?.progress_percentage?.toFixed(0) || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Overall Progress
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <LinearProgress 
              variant="determinate" 
              value={enrollment?.progress_percentage || 0} 
              sx={{ height: 10, borderRadius: 1 }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4">
                    {enrollment?.modules_completed || 0}/{enrollment?.total_modules || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Modules Completed
                  </Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4">
                    {enrollment?.tests_passed || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tests Passed
                  </Typography>
                </Box>
                <QuizIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4">
                    {enrollment?.points_earned || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Points Earned
                  </Typography>
                </Box>
                <TrophyIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4">
                    {formatDuration((enrollment?.total_time_spent || 0) / 60)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Time Spent
                  </Typography>
                </Box>
                <TimerIcon sx={{ fontSize: 40, color: 'info.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Modules Progress */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Course Modules
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {modules && modules.length > 0 ? (
          <Box>
            {modules.map((module, index) => {
              const moduleProgress = module.progress || {};
              const isCompleted = moduleProgress.completed;
              const hasTest = module.requires_knowledge_test;
              const testPassed = moduleProgress.knowledge_test?.passed;

              return (
                <Accordion
                  key={module.id}
                  expanded={expandedModule === module.id}
                  onChange={() => handleModuleExpand(module.id)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" width="100%" gap={2}>
                      {isCompleted ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <RadioButtonUncheckedIcon color="disabled" />
                      )}
                      
                      <Box flexGrow={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Module {index + 1}: {module.title}
                        </Typography>
                        {module.description && (
                          <Typography variant="body2" color="text.secondary">
                            {module.description}
                          </Typography>
                        )}
                      </Box>

                      <Stack direction="row" spacing={1} alignItems="center">
                        {hasTest && (
                          <Chip
                            label={testPassed ? "Test Passed" : "Test Required"}
                            color={testPassed ? "success" : "default"}
                            size="small"
                          />
                        )}
                        {isCompleted && (
                          <Chip
                            label="Completed"
                            color="success"
                            size="small"
                          />
                        )}
                      </Stack>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    {module.submodules && module.submodules.length > 0 ? (
                      <List>
                        {module.submodules.map((submodule) => {
                          const subProgress = moduleProgress.submodules?.[submodule.id] || {};
                          const isSubCompleted = subProgress.completed;

                          return (
                            <ListItem key={submodule.id} sx={{ pl: 4 }}>
                              <ListItemIcon>
                                {isSubCompleted ? (
                                  <CheckCircleIcon color="success" fontSize="small" />
                                ) : (
                                  <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={submodule.title}
                                secondary={
                                  <>
                                    {submodule.description && (
                                      <Typography variant="body2" component="span">
                                        {submodule.description}
                                      </Typography>
                                    )}
                                    {isSubCompleted && subProgress.completed_at && (
                                      <Typography variant="caption" display="block" color="text.secondary">
                                        Completed on {formatDate(subProgress.completed_at)}
                                      </Typography>
                                    )}
                                  </>
                                }
                              />
                              {submodule.estimated_duration && (
                                <Typography variant="caption" color="text.secondary">
                                  {formatDuration(submodule.estimated_duration)}
                                </Typography>
                              )}
                            </ListItem>
                          );
                        })}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No submodules available
                      </Typography>
                    )}

                    {/* Knowledge Test Info */}
                    {hasTest && moduleProgress.knowledge_test && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Knowledge Test
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Attempts
                            </Typography>
                            <Typography variant="body1">
                              {moduleProgress.knowledge_test.attempts || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Best Score
                            </Typography>
                            <Typography variant="body1">
                              {moduleProgress.knowledge_test.best_score?.toFixed(1) || 0}%
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Status
                            </Typography>
                            <Chip
                              label={testPassed ? "Passed" : "Not Passed"}
                              color={testPassed ? "success" : "default"}
                              size="small"
                            />
                          </Grid>
                          {testPassed && moduleProgress.knowledge_test.passed_at && (
                            <Grid item xs={6} sm={3}>
                              <Typography variant="body2" color="text.secondary">
                                Passed On
                              </Typography>
                              <Typography variant="body2">
                                {formatDate(moduleProgress.knowledge_test.passed_at)}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}

                    {isCompleted && moduleProgress.completed_at && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="success.dark">
                          ✓ Module completed on {formatDate(moduleProgress.completed_at)}
                          {moduleProgress.time_spent && (
                            <> · Time spent: {formatDuration(moduleProgress.time_spent / 60)}</>
                          )}
                        </Typography>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        ) : (
          <Alert severity="info">No modules found for this course</Alert>
        )}
      </Paper>

      {/* Last Accessed Info */}
      {enrollment?.last_accessed && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Last accessed: {new Date(enrollment.last_accessed).toLocaleString()}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default EnrollmentProgress;